import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Segment, Header, Container, Image, Grid, Form } from 'semantic-ui-react';
import { UniversalLink } from '@plone/volto/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import { map, isEmpty, uniqBy } from 'lodash';
import { flattenToAppURL } from '@plone/volto/helpers';
import { getTramits } from '../../../actions';

import bgImage from './img/img-procedures.svg';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'What are you looking for?',
    defaultMessage: 'What are you looking for?',
  }
});


const View = (props) => {
  const intl = useIntl();
  const { data, mode } = props;
  const isEditMode = mode === 'edit';
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');

  const allTramits = useSelector((state) => state.tramits?.data || []);

  const cleanString = (str) => {
    if (!str) return '';
    const decoded = str.replace(/\\x([0-9A-Fa-f]{2})/g, (match, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );
    return decoded.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const explicitTramits = (data.tramits || [])
    .map((id) => allTramits.find((t) => t['@id'] === id))
    .filter(Boolean);

  const categorizedTramits = allTramits.filter((t) => {
    const selectedTokens = data.categories || [];
    const itemCats = Array.isArray(t.category) ? t.category : [t.category];

    return selectedTokens.some((token) => {
      const cleanToken = cleanString(token);
      return itemCats.some((cat) => cleanString(cat) === cleanToken);
    });
  });

  const selectedTramits = uniqBy([...explicitTramits, ...categorizedTramits], '@id');

  const searchedTramits = searchQuery.trim()
    ? allTramits.filter((t) =>
        cleanString(t.title).includes(cleanString(searchQuery))
      )
    : null;

  const displayedTramits = searchedTramits ?? selectedTramits;

  useEffect(() => {
    dispatch(getTramits());
  }, [dispatch]);

  return (
    <div className={"secTramits__wrapper d-print-none " + (!isEditMode ? 'full-width' : '')}>
      <Segment as="section" className="secTramits px-0" basic>
        <Container>

          <div className="secTramits__header">
            {data?.title ? (
              <Header as="h2" className="secTramits__title mb-2">
                {data.title}
              </Header>
            ) : (
              <Header className="visually-hidden mb-2" as="h2">
                <FormattedMessage id="Tràmits" defaultMessage="Tràmits" />
              </Header>
            )}

            {data?.description && <p className="secTramits__description mb-0">{data.description}</p>}
          </div>

          <Grid>
            <Grid.Row columns={12}>
              <Grid.Column mobile={12} tablet={8} computer={8}>

                <Form className='secTramits__search'>
                  <Form.Field className='secTramits__search__field'>
                    <FontAwesomeIcon icon={faSearch} className='secTramits__search__icon' />
                    <label className='visually-hidden secTramits__search__label' htmlFor='searchTramitInput'>
                      <FormattedMessage id="What are you looking for?" defaultMessage="What are you looking for?" />
                    </label>
                    <div className='secTramits__search__inputWrapper' style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <input
                        id='searchTramitInput'
                        className='secTramits__search__input'
                        type='search'
                        placeholder={intl.formatMessage(messages.searchPlaceholder)}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button
                          type='button'
                          className='secTramits__search__clear'
                          onClick={() => setSearchQuery('')}
                          aria-label={intl.formatMessage({ id: 'Clear search', defaultMessage: 'Clear search' })}
                          style={{ position: 'absolute', right: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', lineHeight: 1 }}
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      )}
                    </div>
                  </Form.Field>
                </Form>

                {!isEmpty(displayedTramits) && (
                  <>
                    <h3 className='secTramits__listTitle mt-0'>
                      {searchedTramits ? (
                        <FormattedMessage id="Search results" defaultMessage="Search results" />
                      ) : (
                        <FormattedMessage id="Featured procedures" defaultMessage="Featured procedures" />
                      )}
                    </h3>

                    <ul className='secTramits__list list-unstyled'>
                      {map(displayedTramits, (tramit) => (
                        <li key={tramit['@id']} className='secTramits__list__item'>
                          <UniversalLink
                            href={flattenToAppURL(tramit.link)}
                            className="secTramitsItem"
                          >
                            <span className="me-3">{tramit.title}</span>
                            <FontAwesomeIcon icon={faArrowRight} className="ms-auto flex-shrink-0" />
                          </UniversalLink>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {searchedTramits && isEmpty(searchedTramits) && (
                  <p className="secTramits__noResults">
                    <FormattedMessage id="No results found" defaultMessage="No results found" />
                  </p>
                )}

                {!searchQuery && data.linkUrl && (
                  <p className="secTramits__buttons mb-0 pt-3">
                    <UniversalLink href={data.linkUrl} className="primary ui button">
                      {data?.linkTitle}
                      <span className="visually-hidden">
                        <FormattedMessage id="Opens in new window" defaultMessage="Opens in new window" />
                      </span>
                    </UniversalLink>
                  </p>
                )}

              </Grid.Column>
              <Grid.Column mobile={12} tablet={4} computer={4}>
                <Image
                  width="422"
                  height="467"
                  src={bgImage}
                  alt=""
                  loading='lazy'
                  className="secTramits__background d-none d-md-block"
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>

        </Container>
      </Segment>
    </div>
  );
};

export default View;